import { createContext, useContext, useEffect, useState } from "react";
import { createContextualCan } from "@casl/react";
import { useAuth } from "./AuthContext";
import { Ability, AbilityBuilder } from "@casl/ability";

// Define types for ability actions
type AbilityActions = {
  create?: boolean;
  edit?: boolean;
  view?: boolean;
  delete?: boolean;
};

// Define the ability context type
type AbilityContextType = {
  ability: Ability;
  chatAbility: AbilityActions;
  projectAbility: AbilityActions;
  allAbility: AbilityActions;
};

const AbilityContext = createContext<AbilityContextType | undefined>(undefined);
export const Can = createContextualCan(AbilityContext.Consumer as any);

function AbilityProvider({ children }: { children: React.ReactNode }) {
  const { userDetails } = useAuth(); // userDetails contains permissions from Postgres
  const [ability, setAbility] = useState<Ability>(new Ability()); // Initial state for ability
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoaded, setIsLoaded] = useState(false); // State to check if permissions are loaded

  useEffect(() => {
    const defineAbilities = () => {
      const permissions = userDetails?.permissions || []; // Retrieve permissions from userDetails

      const { can, build } = new AbilityBuilder(Ability);

      // Process each permission
      permissions.forEach((permission: any) => {
        permission.actions.forEach((action: string) => {
          can(action, permission.subject);
        });
      });

      setAbility(build()); // Build and set ability
      setIsLoaded(true); // Mark as loaded
    };

    if (userDetails?.permissions) {
      defineAbilities(); // Only run if permissions are available
    }
  }, [userDetails]);

  // if (!isLoaded) {
  //   return <div>Loading permissions...</div>; // Loading state until abilities are set
  // }

  // Define abilities based on the subjects
  const chatAbility = {
    create: ability.can("create", "chat"),
    edit: ability.can("edit", "chat"),
    view: ability.can("view", "chat"),
    delete: ability.can("delete", "chat"),
  };

  const projectAbility = {
    create: ability.can("create", "project"),
    edit: ability.can("edit", "project"),
    view: ability.can("view", "project"),
    delete: ability.can("delete", "project"),
  };

  const allAbility = {
    create: ability.can("create", "All"),
    edit: ability.can("edit", "All"),
    view: ability.can("view", "All"),
    delete: ability.can("delete", "All"),
  };

  return (
    <AbilityContext.Provider
      value={{
        ability,
        chatAbility,
        projectAbility,
        allAbility,
      }}
    >
      {children}
    </AbilityContext.Provider>
  );
}

const useAbilityContext = () =>
  useContext(AbilityContext) as AbilityContextType;

export { AbilityContext, useAbilityContext, AbilityProvider };
